// pages/DriverDetails.tsx
import React from "react";
import { twMerge } from 'tailwind-merge';
import Button from '../components/Button';
import ConfirmationDialog from "../components/ConfirmationDialog";
import PayoutModal from "../components/DriverDetails/PayoutModal";
import RejectReason from "../components/DriverDetails/RejectReason";
import ResubmittedDocuments from "../components/DriverDetails/ResubmittedDocuments";
import EnlargedImage from "../components/Image";
import MapContainer from '../components/Map';
import Rating from "../components/Rating";
import Table from "../components/Table";
import VerifyDriverBackgroundBtn from '../components/VerifyDriverBackgroundBtn';
import useDriverDetails from '../hooks/logics/user-accounts/use-driver-details';
import { formatDate2, formatPrices } from '../utils/helpers';


const DetailRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className="flex flex-col items-start gap-2">
    <p className="text-gray-500 min-w-[140px] text-sm">{label}</p>
    <p className="font-semibold text-sm text-gray-900 break-all">{value ?? "N/A"}</p>
  </div>
);

const ProgressRate = ({ label, value, className }: { label: string; value?: number | null, className?: string }) => (
  <div className="flex flex-col items-start gap-2">
    <div className="flex justify-between w-full items-baseline">
      <p className="text-gray-500 text-sm">{label}</p>
      <p className="text-gray-900 font-semibold text-sm">{value ?? 0}%</p>
    </div>
    <div className="w-full bg-gray-200 rounded h-2">
      <div
        className={twMerge("bg-green-500 h-2 rounded transition-all duration-300", className)}
        style={{ width: `${value ?? 0}%` }}
      />
    </div>
  </div>
);

const DriverDetails = () => {
  const {
    id, details, loading, isModifying, isGettingAddress,
    isMessageVisible, setMessageVisible,
    isPayoutVisible, setPayoutVisible,
    isViewingResubmittedDocuments, setIsViewingResubmittedDocuments,
    isRejectReasonModalVisible, setIsRejectReasonModalVisible,
    driverAddress, image, zoomed, setZoomed,
    getVehicleLabel, handleZoomImage, changeStatus,
    handleGetDriverAddress, refetchDriverDetails,
    isPendingReview, isResubmit,
    driverAnalysis
  } = useDriverDetails();

  return (
    <div className="py-2 md:py-10 h-auto flex flex-col justify-center font-inter bg-gray-50 md:bg-gray-100">

      <div className="w-full p-4 md:p-6 flex flex-col bg-white rounded-none md:rounded-lg border-y md:border border-gray-200 shadow-none md:shadow-sm gap-5 md:gap-6">
        {loading ? (
          <div className="w-full py-10 flex justify-center items-center">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-100 pb-4 gap-4 w-full overflow-hidden">

              <div className="flex items-center gap-3 md:gap-4 w-full md:w-auto">
                <img
                  onClick={() => handleZoomImage(details?.profileImage || "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png")}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png";
                  }}
                  className="w-14 h-14 md:w-20 md:h-20 object-cover border-2 border-gray-200 rounded-full md:rounded-3xl cursor-pointer shrink-0"
                  src={details?.profileImage || "https://www.pngfind.com/pngs/m/610-6104451_image-placeholder-png-user-profile-placeholder-image-png.png"}
                  alt={details?.fullName ? `${details?.fullName} profile` : "Profile placeholder"}
                />

                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex flex-col md:flex-row md:gap-2 md:items-center">
                    <h2 className="text-lg md:text-2xl font-semibold truncate">
                      {details?.fullName || "Unknown User"}
                    </h2>
                    <div className="mt-1 md:mt-0">
                      <Rating ratingcount={details?.ratingCount || 0} rating={details?.rating || 0} />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5 truncate">
                    {details?.email}
                  </p>
                </div>
              </div>

              {!loading && isPendingReview && (
                <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0">
                  <button
                    onClick={() => changeStatus("approved")}
                    disabled={isModifying}
                    className="flex-1 md:flex-none px-4 md:px-8 py-2.5 md:py-2 rounded-xl border border-primary text-primary font-medium disabled:opacity-50 transition-opacity text-sm md:text-base"
                  >
                    Allow
                  </button>
                  <button
                    onClick={() => {
                      if (!details?.isEmailVerified) {
                        setMessageVisible(true);
                        return;
                      }
                      setIsRejectReasonModalVisible(true);
                    }}
                    disabled={isModifying}
                    className="flex-1 md:flex-none px-4 md:px-8 py-2.5 md:py-2 rounded-xl border border-red-600 text-red-600 font-medium disabled:opacity-50 transition-opacity text-sm md:text-base"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse lg:flex-row gap-6 w-full">

              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-y-3 gap-x-4">
                <DetailRow label="Email:" value={details?.email} />
                <DetailRow label="Phone number:" value={details?.phone} />
                <DetailRow label="Joined:" value={formatDate2(details?.createdAt || "")} />
                <DetailRow label="Date of birth:" value={formatDate2(details?.dob || "")} />
                <DetailRow label="License number:" value={details?.driverLicenseNumber} />
                <DetailRow label="State:" value={details?.state} />
                <DetailRow label="Status:" value={details?.isOnline ? "Online" : "Offline"} />
                <DetailRow label="Vehicle number:" value={details?.vehicleNumber} />
                <DetailRow label="Vehicle type:" value={getVehicleLabel(details?.vehicleType || "")} />
                <DetailRow label="Wallet:" value={formatPrices(details?.wallet || 0)} />
                <DetailRow label="Pending clearance:" value={formatPrices(details?.pendingForClearance || 0)} />
                <DetailRow label="Total orders:" value={details?.totalOrders ?? 0} />
                <DetailRow label="Completed orders:" value={details?.completedOrders ?? 0} />
                <DetailRow label="Vehicle make:" value={details?.vehicleMake} />
                <DetailRow label="Vehicle model:" value={details?.vehicleModel} />
                <DetailRow label="Vehicle year:" value={details?.vehicleMakeYear} />
                <DetailRow label="License state:" value={details?.driverLicenseState} />
                <DetailRow label="Notified count:" value={driverAnalysis?.notifiedCount} />
                <ProgressRate label="Cancellation rate:" className='bg-red-500' value={Number(driverAnalysis?.cancellationRate) || 0} />
                <ProgressRate label="Completed rate:" value={Number(driverAnalysis?.completedRate) || 0} />
              </div>

              {/* Right: Map & Verification */}
              <div className="w-full lg:w-72 flex flex-col gap-4 shrink-0">
                {!details?.candidate_id && isPendingReview && (
                  <VerifyDriverBackgroundBtn
                    onSuccess={refetchDriverDetails}
                    id={id || ""}
                    className="!px-4 !py-3 md:!py-2 text-white rounded-lg h-auto w-full font-medium"
                  />
                )}

                {details?.coords?.coordinates?.length && details?.coords?.coordinates?.length > 0 && (
                  <div className="flex flex-col gap-3 w-full">
                    <div className="h-48 md:h-56 rounded-xl overflow-hidden w-full relative border border-gray-200">
                      <MapContainer
                        latt={details.coords.coordinates[1]}
                        lngg={details.coords.coordinates[0]}
                      />
                    </div>
                    <Button isLoading={isGettingAddress} onClick={handleGetDriverAddress} className="w-full py-3 md:py-2 font-medium">
                      Get address
                    </Button>
                    {driverAddress && (
                      <p className="text-sm text-gray-700 mt-1 bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <span className="font-semibold block mb-1">Location Address:</span>
                        {driverAddress}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row flex-wrap items-center gap-3 pt-4 border-t border-gray-100">
              {[
                { label: "Vehicle insurance file", url: details?.vehicleInsuranceFile },
                { label: "Vehicle registration file", url: details?.vehicleRegistrationFile },
                { label: "Driving license file", url: details?.drivingLicenseFile },
              ].filter(f => f.url).map((file) => (
                <a key={file.label} href={file.url} target="_blank" rel="noreferrer" className="w-full md:w-auto">
                  <Button className="px-4 py-3 md:py-2 text-white rounded-lg w-full font-medium">
                    {file.label}
                  </Button>
                </a>
              ))}

              {isResubmit && (
                <Button
                  onClick={() => setIsViewingResubmittedDocuments(true)}
                  className=" px-4 py-3 md:py-2 rounded-lg w-full md:w-auto "
                >
                  View resubmitted documents
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Outer Components & Modals */}
      <Table driverId={id} />
      <EnlargedImage isVisible={zoomed} setVisible={setZoomed} src={image} />

      <PayoutModal
        driver={details?._id}
        inWallet={details?.wallet}
        isVisible={isPayoutVisible}
        onClose={() => setPayoutVisible(false)}
      />

      <ConfirmationDialog
        messageMode={true}
        isVisible={isMessageVisible}
        onHide={() => setMessageVisible(false)}
        text="Email is not verified yet"
        closeButtonText="Close"
      />

      <ResubmittedDocuments
        onApproveOrRejectSuccess={refetchDriverDetails}
        isVisible={isViewingResubmittedDocuments}
        onHide={() => setIsViewingResubmittedDocuments(false)}
        driverId={id}
      />

      <RejectReason
        isLoading={isModifying}
        isVisible={isRejectReasonModalVisible}
        onHide={() => setIsRejectReasonModalVisible(false)}
        onConfirm={(reason) => changeStatus("rejected", reason)}
      />
    </div>
  );
};

export default DriverDetails;